import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { FormError } from '@/components/form/form-error';
import { SelectInput } from '@/components/form/select-input';
import { Modal } from '@/components/modal/modal';
import { ModalActions } from '@/components/modal/modal-actions';
import { formatDecimal } from '@/lib/money';
import { toast } from '@/lib/toast';
import { type PackageItem, useDeletePackage } from '../api/costs';

const measures = (item: PackageItem) =>
  item.lengthCm && item.widthCm && item.heightCm
    ? `${formatDecimal(item.lengthCm, 1)} × ${formatDecimal(item.widthCm, 1)} × ${formatDecimal(item.heightCm, 1)} cm`
    : 'sem medidas';

/**
 * Exclusão de embalagem. Sem produto fixado, confirma e exclui. Com produto fixado, pede a
 * embalagem para onde eles vão — já sugerindo a de mesmas medidas, que é o caso das repetidas — e
 * move antes de excluir: nenhum produto fica sem a embalagem que alguém escolheu para ele.
 */
export function DeletePackageDialog({
  value,
  packages,
  onClose,
}: {
  value: PackageItem | null;
  packages: PackageItem[];
  onClose: () => void;
}) {
  const remove = useDeletePackage();
  const [picked, setPicked] = useState('');
  if (!value) return null;
  const fixed = value.productCount ?? 0;
  const replacement = picked || value.duplicateOf?.id || '';
  const options = packages
    .filter((item) => item.active && item.id !== value.id)
    .map((item) => ({ value: item.id, label: `${item.name} · ${measures(item)}` }));

  function close() {
    setPicked('');
    remove.reset();
    onClose();
  }

  function confirm() {
    if (!value) return;
    remove.mutate(
      { id: value.id, ...(fixed > 0 ? { replaceWith: replacement } : {}) },
      {
        onSuccess: (result) => {
          toast.success(
            result.moved > 0
              ? `Embalagem excluída; ${result.moved} produto(s) passaram para a escolhida.`
              : 'Embalagem excluída.',
          );
          close();
        },
      },
    );
  }

  return (
    <Modal
      open
      onOpenChange={(open) => !open && close()}
      title={`Excluir ${value.name}?`}
      description={
        fixed > 0
          ? `${fixed} produto(s) estão fixados nesta embalagem. Escolha para qual eles vão antes de excluir.`
          : 'Nenhum produto está fixado nela. As margens são recalculadas sem ela.'
      }
      size="md"
      footer={
        <ModalActions
          onCancel={close}
          onConfirm={confirm}
          confirmLabel={fixed > 0 ? `Mover ${fixed} e excluir` : 'Excluir'}
          confirmIcon={<Trash2 aria-hidden />}
          confirmVariant="destructive"
          isConfirmDisabled={fixed > 0 && !replacement}
          isLoading={remove.isPending}
        />
      }
    >
      <div className="space-y-3">
        <FormError error={remove.error} fallback="Não foi possível excluir." />
        {fixed > 0 && (
          <SelectInput
            value={replacement}
            onValueChange={setPicked}
            options={options}
            placeholder="Escolha a embalagem"
            aria-label="Embalagem para os produtos fixados"
          />
        )}
      </div>
    </Modal>
  );
}
